import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Upload, X, Lock, ImagePlus, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { compressImage } from "@/lib/imageCompression";
import { analyzeImage, generateText, track } from "@/lib/api";
import { track as rybbit } from "@/lib/track";
import { ResultView } from "@/components/ResultView";

const PLATFORMS = [
  { key: "general", label: "Allgemein", testId: "platform-tab-allgemein" },
  { key: "kleinanzeigen", label: "Kleinanzeigen", testId: "platform-tab-kleinanzeigen" },
  { key: "facebook", label: "Facebook", testId: "platform-tab-facebook" },
  { key: "vinted", label: "Vinted", testId: "platform-tab-vinted" },
];

const LOADING_TEXTS = ["Foto wird analysiert …", "Fast fertig …", "Dein Text wird erstellt …"];

export const Generator = ({ presetCategory = null, presetTitlePlaceholder }) => {
  const [step, setStep] = useState("input"); // input | questions | result
  const [images, setImages] = useState([]); // [{dataUrl}]
  const [title, setTitle] = useState("");
  const [platform, setPlatform] = useState("general");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState(LOADING_TEXTS[0]);
  const [error, setError] = useState(null);
  const [rateLimited, setRateLimited] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [vision, setVision] = useState(null);
  const [category, setCategory] = useState(presetCategory);
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");

  const [result, setResult] = useState(null);
  const [aiError, setAiError] = useState(false);

  const cycleLoading = () => {
    let i = 0;
    setLoadingText(LOADING_TEXTS[0]);
    const id = setInterval(() => {
      i = (i + 1) % LOADING_TEXTS.length;
      setLoadingText(LOADING_TEXTS[i]);
    }, 1300);
    return () => clearInterval(id);
  };

  // ---------------- Upload ----------------
  const handleFiles = async (fileList) => {
    setError(null);
    const files = Array.from(fileList).slice(0, 2 - images.length);
    for (const file of files) {
      try {
        const dataUrl = await compressImage(file);
        setImages((prev) => (prev.length >= 2 ? prev : [...prev, { dataUrl }]));
        track("image_uploaded");
      } catch (e) {
        setError(e.message || "Bild konnte nicht verarbeitet werden.");
      }
    }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  // ---------------- Analyze ----------------
  const startAnalysis = async () => {
    setError(null);
    if (images.length === 0) {
      setError("Bitte lade mindestens ein Foto hoch.");
      return;
    }
    if (!title.trim()) {
      setError("Bitte gib kurz an, was du verkaufst.");
      return;
    }
    track("tool_started", { presetCategory });
    track("product_title_entered");
    rybbit("calculator_used", { calculator: "listing_text", mode: "default" });
    rybbit("ai_estimate", { status: "triggered" });
    setLoading(true);
    const stop = cycleLoading();
    try {
      const data = await analyzeImage({
        productTitle: title.trim(),
        category: presetCategory,
        platform,
        images: images.map((i) => i.dataUrl),
      });
      setSessionId(data.sessionId);
      setVision(data.visionAnalysis);
      setCategory(data.category);
      setQuestions(data.questions || []);
      setAnswers((data.questions || []).map((q) => ({ questionId: q.id, question: q.question, answer: "", skipped: false })));
      setQIndex(0);
      setStep("questions");
      track("questions_shown", { count: (data.questions || []).length });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setRateLimited(true);
        track("limit_reached");
        rybbit("ai_estimate", { status: "blocked" });
      }
      setError(detail || "Es ist ein Fehler aufgetreten. Bitte versuche es erneut.");
    } finally {
      stop();
      setLoading(false);
    }
  };

  // ---------------- Questions ----------------
  const setAnswerFor = (idx, value, skipped = false) => {
    setAnswers((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], answer: value, skipped };
      return copy;
    });
  };

  const advance = async (finalAnswers) => {
    setCustomMode(false);
    setCustomText("");
    if (qIndex < questions.length - 1) {
      setQIndex(qIndex + 1);
    } else {
      await runGenerate(finalAnswers || answers);
    }
  };

  const onChoose = (option) => {
    if (option === "Eigene Antwort") {
      setCustomMode(true);
      return;
    }
    const updated = [...answers];
    updated[qIndex] = { ...updated[qIndex], answer: option, skipped: false };
    setAnswers(updated);
    track("question_answered", { q: qIndex });
    advance(updated);
  };

  const onSubmitCustom = () => {
    if (!customText.trim()) return;
    const updated = [...answers];
    updated[qIndex] = { ...updated[qIndex], answer: customText.trim(), skipped: false };
    setAnswers(updated);
    track("question_answered", { q: qIndex, custom: true });
    advance(updated);
  };

  const onSubmitText = () => {
    const updated = [...answers];
    // answer already stored via input onChange; treat empty as skip-able
    track("question_answered", { q: qIndex });
    advance(updated);
  };

  const onSkip = () => {
    const updated = [...answers];
    updated[qIndex] = { ...updated[qIndex], answer: "", skipped: true };
    setAnswers(updated);
    track("questions_skipped", { q: qIndex });
    advance(updated);
  };

  const runGenerate = async (finalAnswers) => {
    setLoading(true);
    const stop = cycleLoading();
    try {
      const data = await generateText({
        sessionId,
        productTitle: title.trim(),
        category,
        platform,
        visionAnalysis: vision || {},
        answers: finalAnswers,
      });
      setResult(data.result);
      setAiError(data.aiError);
      setStep("result");
      track("generation_completed", { aiError: data.aiError });
      rybbit("result_shown", { calculator: "listing_text", verdict: "ok" });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || "Die Texterstellung ist gerade nicht verfügbar. Bitte versuche es gleich noch einmal.");
      setStep("questions");
    } finally {
      stop();
      setLoading(false);
    }
  };

  const resetAll = () => {
    setStep("input");
    setImages([]);
    setTitle("");
    setPlatform("general");
    setSessionId(null);
    setVision(null);
    setCategory(presetCategory);
    setQuestions([]);
    setQIndex(0);
    setAnswers([]);
    setResult(null);
    setError(null);
    setAiError(false);
  };

  // ---------------- Render ----------------
  const StepBadge = ({ active, done, children, testId }) => (
    <div data-testid={testId} className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium ${active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"}`}>
      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${active ? "bg-primary text-primary-foreground" : done ? "bg-success/15 text-foreground" : "bg-muted text-muted-foreground"}`}>{done ? "✓" : children?.[0] === "F" ? "1" : "•"}</span>
      <span className="hidden sm:inline">{children}</span>
    </div>
  );

  return (
    <Card className="relative z-10 rounded-2xl border shadow-[0_10px_30px_rgba(15,23,42,0.08)] p-4 sm:p-6" data-testid="generator-card">
      {/* Stepper */}
      <div className="flex items-center gap-3 mb-5" data-testid="generator-stepper">
        <StepBadge testId="generator-step-foto" active={step === "input"} done={step !== "input"}>Foto & Titel</StepBadge>
        <div className="h-px flex-1 bg-border" />
        <StepBadge testId="generator-step-fragen" active={step === "questions"} done={step === "result"}>Fragen</StepBadge>
        <div className="h-px flex-1 bg-border" />
        <StepBadge testId="generator-step-ergebnis" active={step === "result"} done={false}>Ergebnis</StepBadge>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4" data-testid="generator-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-10 text-center" data-testid="generator-loading-state">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground" data-testid="generator-loading-text">{loadingText}</p>
          <div className="max-w-xs mx-auto mt-4"><Progress value={66} /></div>
        </div>
      )}

      {!loading && (
        <AnimatePresence mode="wait">
          {/* STEP 1: INPUT */}
          {step === "input" && (
            <motion.div key="input" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
              <ol className="text-sm text-muted-foreground mb-4 space-y-1">
                <li>1. Lade 1 bis 2 Fotos hoch</li>
                <li>2. Gib kurz an, was du verkaufst</li>
                <li>3. Beantworte bis zu 3 Fragen</li>
              </ol>

              {/* Upload */}
              <div
                data-testid="photo-upload-dropzone"
                className="rounded-2xl border border-dashed bg-secondary/40 p-4 sm:p-6"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              >
                <div className="flex flex-col items-center text-center gap-3">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><ImagePlus className="h-6 w-6" /></span>
                  <div>
                    <Button type="button" data-testid="photo-upload-add-button" onClick={() => document.getElementById("photo-input").click()} disabled={images.length >= 2}>
                      <Upload className="h-4 w-4 mr-2" /> Foto auswählen
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2 hidden sm:block">oder hierher ziehen · max. 2 Fotos</p>
                  </div>
                  <input id="photo-input" data-testid="photo-upload-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
                  <p data-testid="photo-upload-privacy-hint" className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" /> Bitte keine Gesichter, Adressen oder Kennzeichen im Bild.
                  </p>
                </div>

                {images.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    {images.map((img, idx) => (
                      <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-xl overflow-hidden border" data-testid={`photo-upload-preview-${idx}`}>
                        <img src={img.dataUrl} alt={`Vorschau ${idx + 1}`} className="w-full h-32 object-cover" />
                        <button type="button" data-testid={`photo-upload-remove-${idx}`} onClick={() => removeImage(idx)} className="absolute top-1 right-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background/90 border shadow-sm hover:bg-background">
                          <X className="h-4 w-4" />
                        </button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Title */}
              <div className="mt-5">
                <Label htmlFor="product-title" className="font-heading">Was verkaufst du?</Label>
                <Input id="product-title" data-testid="product-title-input" className="h-11 rounded-xl mt-1.5" placeholder={presetTitlePlaceholder || "z. B. ABC Design Salsa 4 Air Kinderwagen"} value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>

              {/* Platform */}
              <div className="mt-5">
                <Label className="font-heading">Plattform (optional)</Label>
                <Tabs value={platform} onValueChange={setPlatform} className="mt-1.5">
                  <TabsList className="grid grid-cols-4 w-full" data-testid="platform-tabs">
                    {PLATFORMS.map((p) => (
                      <TabsTrigger key={p.key} value={p.key} data-testid={p.testId} className="text-xs sm:text-sm">{p.label}</TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>

              <Button className="w-full h-11 mt-6 rounded-[0.85rem] text-base" data-testid="generator-start-button" onClick={startAnalysis}>
                Text vorbereiten <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </motion.div>
          )}

          {/* STEP 2: QUESTIONS */}
          {step === "questions" && questions.length > 0 && (
            <motion.div key={`q-${qIndex}`} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              <div className="flex items-center justify-between mb-3">
                <Badge variant="secondary" data-testid="question-progress">Frage {qIndex + 1}/{questions.length}</Badge>
                <button onClick={onSkip} data-testid="question-skip-button" className="text-sm text-muted-foreground hover:text-foreground">Überspringen</button>
              </div>
              <Card className="p-4 sm:p-5 border" data-testid="question-card">
                <p className="font-heading text-lg font-semibold" data-testid="question-prompt-text">{questions[qIndex].question}</p>

                {questions[qIndex].type === "single-choice" && !customMode && (
                  <div className="grid gap-2 mt-4">
                    {(questions[qIndex].options || []).map((opt) => (
                      <Button key={opt} variant="outline" data-testid="question-option-button" className="h-11 rounded-xl justify-start active:scale-[0.98]" onClick={() => onChoose(opt)}>{opt}</Button>
                    ))}
                  </div>
                )}

                {(questions[qIndex].type === "short-text" || customMode) && (
                  <div className="mt-4">
                    <Textarea
                      data-testid="question-text-answer-input"
                      placeholder="Deine Antwort …"
                      value={customMode ? customText : (answers[qIndex]?.answer || "")}
                      onChange={(e) => (customMode ? setCustomText(e.target.value) : setAnswerFor(qIndex, e.target.value, false))}
                      rows={3}
                    />
                    <Button className="w-full h-11 mt-3 rounded-xl" data-testid="question-next-button" onClick={customMode ? onSubmitCustom : onSubmitText}>
                      Weiter <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </Card>
              <p className="text-xs text-muted-foreground mt-3">Du kannst Fragen auch überspringen, der Text wird dann vorsichtiger formuliert.</p>
            </motion.div>
          )}

          {/* STEP 3: RESULT */}
          {step === "result" && result && (
            <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
              <ResultView
                result={result}
                setResult={setResult}
                sessionId={sessionId}
                category={category}
                initialPlatform={platform}
                aiError={aiError}
                onReset={resetAll}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </Card>
  );
};
